import { useEffect, useRef, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { MapPin, Search } from "lucide-react";

import { MediaFeed } from "@/components/feed/MediaFeed";
import { CuratedCard } from "@/components/feed/CuratedCard";
import { EventCard } from "@/components/feed/EventCard";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { BLUEPRINT_FOR } from "@/lib/blueprint";
import {
  useEventRadar,
  useGenerateMap,
  useGoals,
  useMap,
  usePickEvent,
  usePickedEvents,
  useProfile,
  useRadarScan,
  useSettings,
} from "@/lib/queries";
import { cn } from "@/lib/utils";
import { isRankedGoal } from "@omina/core";
import { CandidateCard, type Candidate } from "@/components/feed/CandidateCard";
import { DayStrip } from "@/components/feed/DayStrip";
import { LocationEditor } from "@/components/feed/LocationEditor";

/**
 * The feed — what is on, for her.
 *
 * A listings layout: one card per room, the facts leading, a heart to keep it.
 * Scannable in a thumb-scroll, which is the only way a founder actually reads
 * this.
 *
 * **The card leads with information, not with a picture, and that is a
 * reversal.** For most of this screen's life a 16/10 tile was the biggest
 * thing on every card — and for most rooms it held a generated gradient, not a
 * photograph, because the base is built from iCal feeds
 * (`packages/core/src/ics.ts`) and iCal carries no cover image. Only a Luma
 * event page's `og:image` and Exa's image on a search result ever hand one
 * over. So the loudest element on a listing was, most of the time, a picture
 * of nothing, while the date, the venue, the fit and the reason to go shared
 * the five lines underneath it.
 *
 * The wash survives at avatar size (`RoomThumb`) — still deterministic, still
 * never a photograph of a different evening. What it stopped being is the
 * headline. The three cards in this stream (`EventCard`, `CuratedCard`,
 * `CandidateCard`) all wear the same shape, which is what lets them sit in one
 * list without any of them reading as a different product.
 *
 * The number that decides the ordering is now on the face of each card. See
 * `RoomFit`: the feed has always been sorted by `goalFit[her track]` and never
 * printed it, so the order of this screen was unexplained on its own surface.
 */
export const Route = createFileRoute("/feed")({
  /**
   * The window the radar sent, if it sent one.
   *
   * A search param rather than shared state so the slice she chose survives a
   * reload and can be linked to — and so this screen still has a sane default
   * when she arrives from anywhere else.
   */
  validateSearch: z.object({
    days: z.number().int().min(1).max(120).optional(),
    /** One day, as `YYYY-MM-DD`. A search param for the same reasons `days`
     *  is one: the day she picked survives a reload and can be linked to. */
    day: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  }),
  component: Feed,
});

/** How many rooms a page shows before she has to ask for more. */
const PAGE = 12;


/**
 * How far ahead the feed looks — one answer, not a row of them.
 *
 * There were four chips (This week / 2 weeks / A month / 6 weeks) and they
 * predate the day strip. Once a founder can tap an actual date, "how many
 * weeks of pool do I want" stopped being a question she should be asked: it is
 * a fetch bound, not a filter she cares about. A month is the honest span —
 * under a week is not enough notice to get into a room, and past a month the
 * far end is mostly unannounced, so a wider window would pad the strip with
 * days nothing has been published for yet.
 *
 * Still a search param, so a link can widen it and the day strip's length
 * follows it. It simply has no control of its own any more.
 */
const WINDOW_DAYS = 30;

/**
 * What she sees before Omina knows who she is aiming at.
 *
 * Two, and the number is doing a specific job. One proves nothing — it reads
 * as the base being empty. A full page proves the product works and removes
 * every reason to answer the questions, and the questions are what makes the
 * ranking hers rather than her industry's.
 *
 * Two is enough to see that these are real rooms with real dates, and few
 * enough that the next thing she wants is more of them.
 *
 * **Nothing here is invented.** The locked rooms are the same ranked list,
 * fetched and scored identically, and the count in the prompt is the count
 * that exists. If the base only has two for her she sees two and no lock,
 * because nothing is being withheld — a gate over an empty pool is the one
 * version of this screen that cannot be recovered from.
 */
const LOCKED_ROOMS = 2;

/**
 * Asks before the button changes what it does.
 *
 * Three, so she gets two real pages and the third press is the one that says
 * "these are not it". Two would interrupt someone still browsing; four is a
 * founder who has already decided the feed is wrong scrolling past a third
 * page to prove it.
 */
const SCANS_BEFORE_QUESTIONS = 3;

function Feed() {
  const { days = WINDOW_DAYS, day } = Route.useSearch();
  const navigate = useNavigate();
  const settings = useSettings();
  const profile = useProfile();
  const goal = settings.data?.activeGoal;

  /**
   * Authority gets a different object, not a different page.
   *
   * Three tracks are answered by rooms with a date on them — customers,
   * funding and learning all mean *be in this room*. Authority does not: it
   * means *get on this show*, and a podcast has no date to sort by, no
   * location to travel to and no going/not-going. Feeding it through the event
   * cards would print "Sat, 15 Aug" on a podcast.
   *
   * Same URL either way, because a founder should not have to know there are
   * two screens — the feed is "what is on for me", and what that means depends
   * on what she is going after.
   */
  const isAuthority = goal === "auth";
  const authorityMap = useMap("auth");
  const outlets = isAuthority
    ? (authorityMap.data?.map?.payload.categories ?? []).flatMap((c) => c.items ?? [])
    : [];

  const radar = useEventRadar(goal, days, Boolean(goal) && !isAuthority, day);
  const picked = usePickedEvents(90);
  const pick = usePickEvent();

  /**
   * A page at a time.
   *
   * Twenty-four cards is a wall — she scrolls past the good ones looking for
   * the end of it. Twelve is about four thumb-flicks, which is a page a person
   * finishes, and "scan more" is then a choice she makes rather than a scroll
   * she endures.
   */
  const [shown, setShown] = useState(PAGE);
  /** How many times she has asked for more this session. */
  const [scans, setScans] = useState(0);
  const all = radar.data?.candidates ?? [];

  /**
   * What the last search turned up.
   *
   * These render in the *same* list as everything else, so the screen is one
   * stream of cards that differ by their badge rather than three sections
   * that differ by their design. They go first because they are the only ones
   * asking her a question — everything below is already hers and can wait.
   */
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const dropCandidate = (url: string) =>
    setCandidates((cs) => cs.filter((c) => c.url !== url));

  /**
   * The two ways to get more rooms, at the top where the question is asked.
   *
   * Both used to be sections *under* the feed — "From the curated base" and
   * "Look further afield" — which put the compounding move and the only way
   * to reach rooms off every watched calendar below everything a founder
   * scrolls. They are not content, they are the two answers to "is this all
   * there is", so they belong beside the window she is already choosing.
   *
   * They are also the only two controls on this screen that spend money, which
   * is why they sit together: whatever a credit eventually buys, it buys one
   * of these. See `docs/credits-model.md`.
   */
  const scan = useRadarScan();
  const generate = useGenerateMap();
  const [scanNote, setScanNote] = useState<string | null>(null);

  /** The city editor, opened from the header pill or from the empty state
   *  that exists because the field is blank. One instance for both, so the
   *  two entry points cannot end up showing two different drafts. */
  const [editingLocation, setEditingLocation] = useState(false);

  async function runScan() {
    if (!goal) return;
    setScanNote(null);
    setCandidates([]);
    try {
      const result = await scan.mutateAsync(goal);
      setCandidates(result.candidates);
      if (!result.candidates.length) {
        // The API tells "nothing new" apart from "nothing at all" and says
        // which; that sentence is better than anything written here.
        setScanNote(
          "note" in result && result.note
            ? result.note
            : "Nothing new out there this time. The calendars above keep filling on their own.",
        );
      }
    } catch {
      // Rendered from `scan.error`; swallowed only so the rejection is handled.
    }
  }

  /**
   * The curated base, in the stream rather than under it.
   *
   * `stale` is rule #2's guard and it survives the move intact: a map composed
   * for clinics must not be read as current by a founder now selling into
   * SaaS, so the rooms are **withheld** rather than annotated — a page of the
   * wrong industry's rooms with a warning on top is still a page she reads.
   */
  const ranked = Boolean(goal) && isRankedGoal(goal!) && !isAuthority;
  const curatedMap = useMap(ranked ? goal! : "cust");
  const curatedStale = ranked ? Boolean(curatedMap.data?.stale) : false;
  const curated =
    ranked && !curatedStale
      ? (curatedMap.data?.map?.payload.categories ?? [])
          .flatMap((c) => c.items ?? [])
          // A room the ranking scored at nothing is a non-result, not a weak
          // one — it is here because it exists, not because it matches her.
          .filter((i) => i.fit > 0)
      : [];
  /**
   * The curated base composes itself, once.
   *
   * It used to be a button beside the web search, which put two controls
   * side by side that a founder had no way to tell apart — and the one that
   * merely re-reads rooms a human already verified is not a decision she
   * should be asked to make. So: if she has an industry to compose against
   * and this track has no map yet, Omina makes one and the rooms arrive in
   * the stream.
   *
   * Guarded by a ref rather than by `isPending`, and per goal. Composition
   * always writes a row — even an empty map is written, with `mode: "empty"`
   * — so a successful run cannot loop. The ref covers the other case: a call
   * that *fails* writes nothing, and without it every re-render of a broken
   * moment would try again and spend again.
   */
  const composed = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!ranked || !goal) return;
    if (!profile.data?.targetIndustry) return;
    if (!curatedMap.isSuccess && !curatedMap.isError) return;
    if (curatedMap.data?.map) return;
    if (composed.current.has(goal)) return;
    composed.current.add(goal);
    generate.mutate(goal);
  }, [ranked, goal, profile.data?.targetIndustry, curatedMap.isSuccess, curatedMap.isError, curatedMap.data?.map, generate]);

  /**
   * Does Omina know enough to rank these for *her*?
   *
   * Two ways of knowing, and both are real answers rather than a flag set by
   * having visited a screen. Three of the four tracks record their blueprint
   * as a goal profile, which `/api/goals` already reports as `hasProfile`. The
   * customers track writes the buyer onto her profile instead, because that is
   * where the radar reads it from — so `buyerRole` is that track's copy of the
   * same fact.
   *
   * Deliberately not a fifth column saying "did the blueprint": that would be
   * a record of a *visit*, and it would drift the first time someone answered
   * the questions somewhere else. This asks whether the answers are there.
   *
   * Unknown resolves to locked, but only once the queries have actually
   * answered — see `settled` below. Guessing "unlocked" while they are in
   * flight would flash the whole feed and then take it away.
   */
  const goals = useGoals();
  const settled = goals.isSuccess && profile.isSuccess;
  const knowsHer =
    Boolean(goals.data?.goals.find((g) => g.type === goal)?.hasProfile) ||
    Boolean(profile.data?.buyerRole);
  const locked = settled && !knowsHer;

  /**
   * One stream, ordered by fit.
   *
   * Dated events and undated curated rooms in the same list, sorted by how
   * well each serves the track she is on. A fund has no evening attached and
   * a meetup does, which is exactly why they cannot be two sections: the
   * question this screen answers is "where should I be", and the answer does
   * not sort into "things with dates" and "things without".
   *
   * The sort is stable and keyed on score alone, so the server's score-then-
   * date ordering survives inside each score — and an unread room still sorts
   * below a scored one at -1, because "not read yet" and "read and poor" are
   * different facts.
   */
  const stream = [
    ...all.map((e) => ({ key: e.id, score: e.score ?? -1, event: e, item: null })),
    ...curated.map((i) => ({
      key: `curated:${i.offerId ?? i.name}`,
      score: i.fit,
      event: null,
      item: i,
    })),
  ].sort((a, b) => b.score - a.score);

  const rooms = stream.slice(0, locked ? LOCKED_ROOMS : shown);
  const more = stream.length - rooms.length;

  /**
   * The third ask goes to the questions instead of to more rooms.
   *
   * Two pages in, showing her a third is the wrong answer to what she is
   * actually saying. "Scan more" the third time means *these are not it* — and
   * more of the same ranking cannot fix a ranking that is not landing. What
   * fixes it is knowing more about her, which is what the blueprint asks.
   *
   * Running out early counts as the same signal, and arrives sooner: if there
   * is nothing left to reveal, another page is not on the table at all, so the
   * questions are the only honest thing the button can do.
   */
  const asksQuestions = scans >= SCANS_BEFORE_QUESTIONS - 1 || more === 0;
  const blueprint = goal ? BLUEPRINT_FOR[goal] : null;
  /** `id` here is the event's own id, the same one the radar returns — which
   *  is what makes the heart's state match across the two screens. */
  const savedIds = new Set((picked.data ?? []).map((p) => p.id));

  return (
    <div className="feedwrap">
      <header className="feedhead">
        <h1 className="feedtitle">{isAuthority ? "Where to be heard" : "New for you"}</h1>
        {/* Her region, not a picker. The base is curated per region and there
            is exactly one she is in; a dropdown offering cities the base does
            not cover would be rule #2 wearing a chevron.

            Tappable, though, and that is not the same thing: what is refused
            above is a list of cities the base does not cover, and what this
            opens is the free-text answer the whole empty state is computed
            from. She moves, or she skipped the question in intake, and this is
            the screen where she finds out it mattered.

            Hidden on the authority surface: a podcast has no city, and a place
            name over a list of shows is a filter that is not being applied. */}
        {!isAuthority && profile.isSuccess && (
          <button
            type="button"
            className="feedplace"
            onClick={() => setEditingLocation((open) => !open)}
            aria-expanded={editingLocation}
          >
            <MapPin size={13} aria-hidden />
            {/* The pin is the one place the city is set *and* changed, so it
                has to exist when there is no city — otherwise the founder the
                empty state is talking to has nothing to press.

                In its own span so a long city truncates inside the button
                instead of stretching it past the edge of a phone. */}
            <span className="feedplacename">
              {profile.data?.location ?? "Set your city"}
            </span>
          </button>
        )}
      </header>

      {!isAuthority && editingLocation && (
        <LocationEditor
          location={profile.data?.location ?? null}
          onClose={() => setEditingLocation(false)}
        />
      )}

      {/*
        Which day — the other question the window chips could not express.

        They set how far ahead to *look*; this picks a day inside that. "All"
        is the default and is what the feed has always shown: everything in the
        window, ranked by fit. Absent on the authority surface for the same
        reason the window chips are — a podcast has no date to pick.
      */}
      {!isAuthority && (
        <DayStrip
          days={days}
          value={day ?? null}
          onChange={(next) =>
            void navigate({ to: "/feed", search: { days, ...(next ? { day: next } : {}) } })
          }
        />
      )}

      {/*
        One button, and only one.

        There were two for a while — this, and "compose my curated base" — and
        they read as alternatives without saying how they differed: one goes
        out to the live web and costs money, the other re-reads rows a human
        already verified and costs a model call. A founder should not have to
        hold that distinction to use her own feed, and the curated base is not
        an action anyway. It is content, so it composes itself (see the effect
        above) and its rooms arrive in the stream like everything else.

        What is left is the only genuinely optional move on the screen: going
        past everything Omina already watches.
      */}
      {!isAuthority && goal && (
        <div className="feedfind">
          <button
            type="button"
            className={cn("btn ghost", scan.isPending && "loading")}
            disabled={scan.isPending}
            onClick={() => void runScan()}
          >
            <span className="sp" aria-hidden />
            <Search size={14} aria-hidden />
            {scan.isPending ? "Searching…" : "Search the web"}
          </button>
        </div>
      )}
      {scan.isError && <p className="danger">{scan.error.message}</p>}
      {generate.isError && <p className="danger">{generate.error.message}</p>}
      {scanNote && <p className="hint">{scanNote}</p>}

      {/* Rule #2's guard, carried over from the section this replaced: the
          rooms are withheld rather than annotated, because a page of the wrong
          industry's rooms with a warning on top is still a page she reads. */}
      {curatedStale && curatedMap.data && (
        <p className="hint">
          Your curated rooms were composed for{" "}
          <b>{curatedMap.data.composedFor}</b> and you now sell into{" "}
          <b>{curatedMap.data.sellingInto}</b>, so Omina is holding them back
          rather than showing you rooms for the wrong buyers. Recompose above.
        </p>
      )}

      {/* The same lock, on the other kind of room. An authority founder who
          has not answered her blueprint is in exactly the position the gate is
          about — the outlets are ranked against her industry alone — so
          leaving this side open would have made the gate a property of which
          track she picked. */}
      {isAuthority && (
        <>
          {authorityMap.isPending ? (
            <p className="lede">Looking…</p>
          ) : (
            <MediaFeed items={locked ? outlets.slice(0, LOCKED_ROOMS) : outlets} />
          )}
          {locked && blueprint && outlets.length > 0 && (
            <div className="feedmore">
              <p className="hint">
                {outlets.length > LOCKED_ROOMS
                  ? `${outlets.length - LOCKED_ROOMS} more ${
                      outlets.length - LOCKED_ROOMS === 1 ? "outlet" : "outlets"
                    } in the base.`
                  : "More outlets as they are curated."}{" "}
                Tell Omina who you are trying to reach and they get ranked for
                you.
              </p>
              <Link to={blueprint} className="btn">
                Unlock my rooms
              </Link>
            </div>
          )}
        </>
      )}

      {!isAuthority && radar.isPending && <p className="lede">Looking…</p>}

      {/*
        A day she picked with nothing on it — a different fact from "the base
        does not cover your area", and it must not borrow that screen's words.
        The window is one tap away and is named, because "nothing on Tuesday"
        is only useful next to "here is where everything is".
      */}
      {!isAuthority && !radar.isPending && rooms.length === 0 && day && (
        <div className="feedempty">
          <h2 className="tk">Nothing on {fullDay(day)}</h2>
          <p className="lede">
            The calendars Omina watches have no rooms that day. That is the
            honest answer — nothing is being held back.
          </p>
          <button
            type="button"
            className="btn ghost"
            onClick={() => void navigate({ to: "/feed", search: { days } })}
          >
            Show the whole window
          </button>
        </div>
      )}

      {/*
        Three empty screens, not one — and they had all been printing the same
        sentence.

        The API has always distinguished them: `mode` says whether it even knew
        where to look, and `watching` counts the calendars being read for her
        region. The feed ignored both and told every founder that the base did
        not cover her city, which for the one who simply never answered "where
        are you?" is not true — her city may well be covered, and the screen was
        blaming the base for a blank field while offering nothing to fix it.
        Intake does ask, but it does not require an answer, so this is a state
        real founders reach.

        An honest empty state is the deepest rule in this product, and an empty
        state that is honest in shape while wrong in fact is worse than a blunt
        one: it sends her to look for the problem in the wrong place.
      */}
      {!isAuthority && !radar.isPending && rooms.length === 0 && !day && (
        <div className="feedempty">
          {radar.data?.mode === "no_location" ? (
            <>
              <h2 className="tk">Omina does not know where you are</h2>
              <p className="lede">
                Event calendars are matched by region, so the feed has nothing to
                match on until you say which city. This is a blank field, not a
                verdict about your area.
              </p>
              {/* No button here. The city is set from the pin at the top of
                  this screen, and a second control for one setting is how two
                  entry points start drifting — this says where it is. */}
              <p className="hint">Set it from the pin above.</p>
            </>
          ) : radar.data?.watching === 0 ? (
            <>
              <h2 className="tk">Nothing on yet</h2>
              <p className="lede">
                Omina watches event calendars by region, and the base does not
                cover {radar.data?.location ?? "your area"} yet. An empty feed is
                the honest answer until it does.
              </p>
              {/* No link out to a fix that does not exist: curating a region is
                  our work, not hers. The one thing she can do — look past the
                  calendars entirely — is the button already at the top. */}
              <p className="hint">
                Searching the web above looks past the calendars Omina watches.
              </p>
            </>
          ) : (
            <>
              <h2 className="tk">A quiet stretch</h2>
              <p className="lede">
                Omina is watching{" "}
                {radar.data?.watching === 1
                  ? "one calendar"
                  : `${radar.data?.watching ?? 0} calendars`}{" "}
                around {radar.data?.location ?? "you"} and none of them has a
                room in this window that fits your track. Nothing is being held
                back.
              </p>
              <p className="hint">
                Searching the web above looks past the calendars Omina watches.
              </p>
            </>
          )}
        </div>
      )}

      {/* Events only — the authority surface rendered above. */}
      {!isAuthority && (
      <ul className="feedlist">
        {/* Scanned candidates lead: they are the only cards asking her to
            decide something. Same card, different badge — which is the whole
            point of merging the sections. */}
        {goal &&
          candidates.map((candidate) => (
            <li key={candidate.url}>
              <CandidateCard
                candidate={candidate}
                goal={goal}
                onResolved={dropCandidate}
              />
            </li>
          ))}
        {rooms.map((row) => {
          // A curated room — a fund, a podcast, a conference a human vouched
          // for. Same card, no heart: there is no evening to add to a week.
          if (row.item) {
            return (
              <li key={row.key}>
                <CuratedCard item={row.item} goal={goal} />
              </li>
            );
          }
          const r = row.event!;
          const saved = savedIds.has(r.id);
          return (
            <li key={row.key}>
              <EventCard
                event={r}
                goal={goal}
                saved={saved}
                busy={pick.isPending}
                onToggle={(going) => pick.mutate({ id: r.id, going })}
              />
            </li>
          );
        })}
      </ul>
      )}

      {/*
        One button, two jobs — and it says which one it is about to do.

        A control labelled "show more rooms" that opens a questionnaire would be
        mislabelled, so on the ask that leads to questions the label changes and
        a line above explains why. The flow is the same either way; what is not
        allowed is the button lying about where it goes.

        It used to say "scan more rooms" while a second control further down
        said "scan for rooms" — one revealing rooms already fetched, the other
        spending money on a live web search. Two controls, one word, opposite
        costs. This one only ever reveals what is already here.
      */}
      {!isAuthority && rooms.length > 0 && (
        <div className="feedmore">
          {locked && blueprint ? (
            /*
              The lock, and it is the same conversation the "sharpen" prompt
              below opens — tier one rather than tier two, because she has not
              answered the first five yet.

              Phrased as what is waiting rather than as what is forbidden. The
              rooms are already ranked; what is missing is the thing that would
              rank them for her, and she is the only one who has it.
            */
            <>
              <p className="hint">
                {more > 0
                  ? `${more} more ${more === 1 ? "room" : "rooms"} on in your area.`
                  : "More rooms as they are found."}{" "}
                Tell Omina who you are trying to reach and they get ranked for
                you.
              </p>
              <Link to={blueprint} className="btn">
                Unlock my rooms
              </Link>
            </>
          ) : asksQuestions && blueprint ? (
            <>
              <p className="hint">
                {more === 0
                  ? "That is everything on in your area right now."
                  : "Still not the right rooms?"}{" "}
                A few questions make the next ones sharper.
              </p>
              {/* Tier two, not the questions she already answered. Sending her
                  back through the first five would read as the product having
                  lost them — and would collect nothing it does not already
                  have. */}
              <Link to={blueprint} search={{ tier: 2 }} className="btn ghost">
                Sharpen my matches
              </Link>
              {/* The other honest reason a feed stops landing: the goal it is
                  ranked against is not the one she is working on any more. */}
              {more === 0 && (
                <p className="hint">
                  Or if the work has moved on, <Link to="/profile">change your path</Link> and
                  everything re-ranks against the new one.
                </p>
              )}
            </>
          ) : (
            <button
              type="button"
              className="btn ghost"
              onClick={() => {
                setShown((n) => n + PAGE);
                setScans((n) => n + 1);
              }}
            >
              Show more rooms
            </button>
          )}
        </div>
      )}

      {/*
        What is left at the bottom is only what belongs at the bottom.

        The curated base and the web search used to sit down here, which put
        the two answers to "is this all there is" below everything a founder
        scrolls. Both are now controls at the top, next to the window, and the
        curated rooms themselves are cards in the one stream above.

        What stays is the industry gate — a prompt, and only while it is
        unanswered. Which calendars feed the base is curation, not a founder's
        decision, so it is not on this screen at all: she reads the rooms, a
        human curates where they come from.
      */}
      <div className="feedtools">
          {/* Rule #2's hard gate, carried over intact. Without the industry
              the base cannot tell a room full of her buyers from a room full
              of other founders, and it will not guess. */}
          {profile.isSuccess && !profile.data?.targetIndustry && blueprint && (
            <Card>
              <CardContent className="space-y-3">
                <h2 className="font-display text-lg">Tell Omina what you sell into.</h2>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  These rooms are ranked from your industry and your goal.
                  Without the industry it cannot tell a room full of your buyers
                  from a room full of other founders — and it will not guess.
                </p>
                <Button asChild size="sm">
                  <Link to={blueprint}>Answer three questions</Link>
                </Button>
              </CardContent>
            </Card>
          )}

        </div>
    </div>
  );
}

/** "Wednesday 26 August" — for the one-day empty state, which has to name the
 *  day it is reporting on or it reads as the feed being broken. */
function fullDay(key: string): string {
  const [y, m, d] = key.split("-").map(Number);
  if (!y || !m || !d) return "that day";
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
}
